import { PersistentMessagesContext } from './persistent-messages-context';
import type { PersistentMessage } from './types';

import { useCallback, useEffect, useMemo, useState } from 'react';

// ----------------------------------------------------------------------

const DISMISSED_MESSAGES_KEY = 'dismissedMessages';

const getDismissedMessages = (): string[] => {
  try {
    const dismissed = localStorage.getItem(DISMISSED_MESSAGES_KEY);
    return dismissed ? JSON.parse(dismissed) : [];
  } catch (error) {
    console.error('Error loading dismissed messages from localStorage:', error);
    return [];
  }
};

const saveDismissedMessages = (messageIds: string[]) => {
  try {
    localStorage.setItem(DISMISSED_MESSAGES_KEY, JSON.stringify(messageIds));
  } catch (error) {
    console.error('Error saving dismissed messages to localStorage:', error);
  }
};

export const PersistentMessagesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [messages, setMessages] = useState<PersistentMessage[]>([]);
  const [dismissedMessageIds, setDismissedMessageIds] = useState<string[]>([]);

  // Load dismissed messages from localStorage on mount
  useEffect(() => {
    setDismissedMessageIds(getDismissedMessages());
  }, []);

  const addMessage = useCallback(
    (message: PersistentMessage, force?: boolean) => {
      setMessages((prevMessages) => {
        // Prevent adding duplicate messages with the same ID
        const messageExists = prevMessages.some((m) => m.id === message.id);
        if (messageExists) {
          return prevMessages;
        }
        return [...prevMessages, message];
      });

      // when force is true, we remove the message from the dismissed messages so it is shown
      if (force) {
        setDismissedMessageIds((prevDismissedIds) => {
          const updatedDismissedIds = prevDismissedIds.filter(
            (id) => id !== message.id,
          );
          if (updatedDismissedIds.length !== prevDismissedIds.length) {
            saveDismissedMessages(updatedDismissedIds);
            return updatedDismissedIds;
          }
          return prevDismissedIds;
        });
      }
    },
    [],
  );

  const removeMessage = useCallback((messageId: string) => {
    setMessages((prevMessages) =>
      prevMessages.filter((message) => message.id !== messageId),
    );

    // Also remove from dismissed messages if it was dismissed
    setDismissedMessageIds((prevDismissedIds) => {
      const updatedDismissedIds = prevDismissedIds.filter(
        (id) => id !== messageId,
      );
      if (updatedDismissedIds.length !== prevDismissedIds.length) {
        saveDismissedMessages(updatedDismissedIds);
        return updatedDismissedIds;
      }
      return prevDismissedIds;
    });
  }, []);

  const dismissMessage = useCallback((messageId: string) => {
    setDismissedMessageIds((prevDismissedIds) => {
      const updatedDismissedIds = [...prevDismissedIds, messageId];
      saveDismissedMessages(updatedDismissedIds);
      return updatedDismissedIds;
    });
  }, []);

  // Filter out dismissed messages
  const visibleMessages = useMemo(
    () =>
      messages.filter((message) => !dismissedMessageIds.includes(message.id)),
    [messages, dismissedMessageIds],
  );

  const contextValue = useMemo(
    () => ({
      state: { messages: visibleMessages },
      actions: { addMessage, removeMessage, dismissMessage },
    }),
    [visibleMessages, addMessage, removeMessage, dismissMessage],
  );

  return (
    <PersistentMessagesContext.Provider value={contextValue}>
      {children}
    </PersistentMessagesContext.Provider>
  );
};
